import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { deleteCart, getCart } from '@apiCalls';
import { CtaContainer, InputField } from '@components';
import { ESCROW_EXPIRY_MINUTES, FRONTEND_BASE_URL } from '@constants';
import { useAppContext } from '@contexts';
import { currencyLookup, formatValue } from '@utils';
import { handleError, withErrorHandler } from '@utils/errorHandler';
import tracker from '@utils/tracking';

const StripeCardForm = () => {
  const {
    cartId,
    event,
    eventSeries,
    ticketDetails,
    ticketDetails: { billingDetails, tickets },
    setEscrowExpiryTime,
    setPaymentCompleted,
  } = useAppContext();
  const router = useRouter();
  const elements = useElements();
  const stripe = useStripe();
  const [blocked, setBlocked] = useState(true);
  const [loading, setLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [error, setError] = useState<string | boolean>(true);
  const [didFail, setDidFail] = useState(false);
  const deleteCartMutation = useMutation({
    mutationFn: deleteCart,
  });

  useEffect(() => {
    tracker.track({
      event: 'InitiateCheckout',
      platforms: ['meta'],
    });
  }, []);

  const { data: cartData } = useQuery({
    queryKey: ['cart', cartId],
    queryFn: () => getCart(cartId ?? ''),
    enabled: !!cartId,
  });

  const formEntriesValid = useCallback(
    withErrorHandler(() => {
      if (cardholderName === '') {
        return setBlocked(true);
      }

      return setBlocked(false);
    }),
    [cardholderName, setBlocked]
  );

  const reset = withErrorHandler(() => {
    setDidFail(true);
    if (cartId) {
      deleteCartMutation.mutate(cartId);
    }
    setEscrowExpiryTime(dayjs().add(ESCROW_EXPIRY_MINUTES, 'minutes').toDate());
    setCardholderName('');
    if (tickets) {
      setTimeout(() => {
        router.replace(`/event/${event.ID}/checkout`);
      }, 3000);
    }
  });

  const currency = ticketDetails.tickets
    ? ticketDetails.tickets[0].Currency
    : 'EUR';
  const totalAmount = cartData?.TotalAmount;
  const handleSubmit = async (uiEvent: React.MouseEvent) => {
    uiEvent.preventDefault();

    if (!cardholderName) return setError('Incomplete billing details');
    if (!stripe || !elements) return;

    const element = elements.getElement(PaymentElement);

    if (!element) return handleError(new Error('Payment Element not found'));

    setLoading(true);

    tracker.track({
      event: {
        event: 'Payment Attempted',
        category: 'Payment',
        value: eventSeries?.ID ?? event.ID,
      },
      platforms: ['ga'],
    });

    const response = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${FRONTEND_BASE_URL}/stripe-success?eventId=${
          event.ID || eventSeries?.ID
        }`,
        payment_method_data: {
          billing_details: {
            name: cardholderName,
            email: billingDetails?.Email,
            phone: billingDetails?.Phone,
          },
        },
      },
      redirect: 'if_required',
    });
    if (response.error) {
      tracker.track({
        event: {
          event: 'Payment Failed',
          category: 'Payment',
          label: `Amount: ${currencyLookup(currency)}${formatValue(
            totalAmount
          )}`,
          value: eventSeries?.ID ?? event.ID,
        },
        platforms: ['ga'],
      });
      setError(`Payment Failed: ${response.error.message}`);
      if (
        response.error.type !== 'card_error' &&
        response.error.type !== 'validation_error'
      ) {
        reset();
      }
    } else {
      if (response.paymentIntent.status === 'succeeded') {
        tracker.track({
          event: {
            event: 'Payment Successful',
            category: 'Payment',
            label: `Amount: ${currencyLookup(currency)}${formatValue(
              totalAmount
            )}`,
            value: eventSeries?.ID ?? event.ID,
          },
          platforms: ['ga'],
        });
        if (event && cartId && tickets && tickets.length > 0) {
          if (cartData) {
            tracker.trackPurchase(cartData, tickets, event, currency);
          }
          setPaymentCompleted(true);
          router.push(`/event/${event.ID || eventSeries?.ID}/checkout/contact`);
        }
        // NOTE previously transaction confirmation was here (API.post(`Transactions/${transactionId}/Confirmation`))
      } else if (response.paymentIntent.status === 'requires_action') {
        tracker.track({
          event: {
            event: 'Payment Failed',
            category: 'Payment',
            label: `Amount: ${currencyLookup(currency)}${formatValue(
              totalAmount
            )}`,
            value: eventSeries?.ID ?? event.ID,
          },
          platforms: ['ga'],
        });
        setError('Some more action is required');
        handleError(new Error('Stripe response: requires_action'), {
          tags: {
            error: response.paymentIntent.status,
            billingDetails: { cardholderName }.toString(),
          },
        });
        reset();
      } else {
        tracker.track({
          event: {
            event: 'Payment Failed',
            category: 'Payment',
            label: `Amount: ${currencyLookup(currency)}${formatValue(
              totalAmount
            )}`,
            value: eventSeries?.ID ?? event.ID,
          },
          platforms: ['ga'],
        });
        setError('Some other error occurred');
        handleError(new Error('Stripe response: other failure'), {
          tags: {
            error: response.paymentIntent.status,
            billingDetails: { cardholderName }.toString(),
          },
        });
        reset();
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    setCardholderName(
      billingDetails?.FirstName
        ? `${billingDetails?.FirstName} ${billingDetails?.LastName}`
        : ''
    );
  }, [billingDetails]);

  useEffect(() => {
    formEntriesValid();
  }, [blocked, formEntriesValid]);

  return (
    <>
      <form
        id='formBilling'
        style={{ padding: '15px 2px 10px', height: '80%' }}
      >
        <div className='form-row'>
          <InputField
            autoComplete='given-name'
            type='text'
            id='cardholderName'
            label='Cardholder Name'
            value={cardholderName}
            onChange={uiEvent => {
              setError(false);
              const target = uiEvent.target as HTMLTextAreaElement;
              setCardholderName(target.value ?? '');
            }}
            required
          />
        </div>
        <div className='form-row' style={{ marginTop: '15px' }}>
          <PaymentElement
            onChange={async uiEvent => {
              setError(!uiEvent.complete);
              setBlocked(!uiEvent.complete);
            }}
          />
        </div>
      </form>
      {error && (
        <div style={{ marginTop: '15px' }} className='error-message'>
          {error}
          <br />
          {didFail && <span>You'll need to reserve a new ticket...</span>}
        </div>
      )}
      <CtaContainer
        blocked={!!error || blocked}
        loading={loading}
        buttonText={`Pay Now (${currencyLookup(currency)}${formatValue(
          totalAmount
        )})`}
        onButtonClick={withErrorHandler(handleSubmit)}
      />
    </>
  );
};

export default StripeCardForm;
